import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "components/DesignSystem/Loader/loader";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { Price } from "components/Price";
import { Form, Formik } from "formik";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { SALES_TAX_FILING } from "constants/addons";
import { US_UUID } from "constants/regAgent";
import { useAddProductToCartMutation } from "store/apis/products";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { useGetCountryStatesQuery } from "store/apis/teamSetting";
import {
  setAddonData,
  setAddonPayload,
  setAddonType,
  setOpenCheckoutModal,
} from "store/slices/addons";
import { setPaymentTitle, setUsableCredit } from "store/slices/credit";
import { RootState } from "store/store";
import { GroupSubscriptionState } from "types/Models/subscription";
import { BackendError } from "types/utils/error";

type SalesTaxFilingFormValues = {
  selectedState: OptionData | null;
};

export const SalesTaxFilingSubscribe = () => {
  const { alertToast, successToast } = useToast();
  const dispatch = useDispatch();
  const { addonData } = useSelector((state: RootState) => state.addonsSlice);
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const [showAddonModal, setShowAddonModal] = useState<boolean>(
    addonData?.subscription_type === SALES_TAX_FILING
  );

  const {
    subscription_name,
    billing_cycle,
    amount,
    stripe_coupon,
    group_subscription,
  } = addonData || {};

  const updatedAmount = stripe_coupon?.discounted_amount.toString() || amount;

  const isCartSubscriptionActive = useAppSelector(
    (state) => state.cartSubscription.isCartSubscriptionActive
  );

  const {
    data: states,
    isLoading,
    isFetching,
    isSuccess,
  } = useGetCountryStatesQuery({ countryId: US_UUID });

  const { data: subscriptions } = useGetSubscriptionsQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );

  const [addToCart, { isLoading: isAddingToCart }] =
    useAddProductToCartMutation();

  const subscribedStates =
    group_subscription?.map(
      (state: GroupSubscriptionState) => state.sales_tax_state_name
    ) || [];

  const stateOptions: OptionData[] =
    states?.map((state: { name: string }) => {
      const isSubscribed = subscribedStates.includes(state.name);
      return {
        label: state.name,
        value: state.name,
        disableOption: isSubscribed,
        data: isSubscribed
          ? "You already have a Sales Tax Filing subscription for this state"
          : undefined,
      };
    }) || [];

  const handleClose = () => {
    setShowAddonModal(false);
    dispatch(setAddonData(null));
    dispatch(setAddonType(""));
  };

  const handleAddToCart = async (stateName: string) => {
    const subscription = subscriptions?.find((s) => s.uuid === addonData?.uuid);

    if (!groupId || !addonData?.uuid || !subscription) {
      return;
    }
    try {
      await addToCart({
        groupId,
        entityId,
        payload: {
          product_id: addonData.uuid,
          product_content_type_id: subscription.product_content_type_id,
          sales_tax_state_name: stateName,
        },
      }).unwrap();
      successToast({
        message: `${subscription_name} - ${stateName} subscription added to cart`,
      });
      handleClose();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Something went wrong",
      });
    }
  };

  const handlePayAndSubscribe = (stateName: string) => {
    dispatch(
      setAddonPayload({
        title: addonData?.subscription_name,
        amount: updatedAmount,
        uuid: addonData?.uuid,
        type: addonData?.subscription_type,
        sales_tax_state_name: stateName,
      })
    );
    dispatch(setUsableCredit(false));
    dispatch(
      setPaymentTitle(`${addonData?.subscription_name!} - ${stateName}`)
    );
    dispatch(setAddonType(""));
    dispatch(setOpenCheckoutModal());
    setShowAddonModal(false);
  };

  const handleSubmit = async (values: SalesTaxFilingFormValues) => {
    const stateName = values.selectedState?.value;
    if (!stateName) {
      return;
    }
    if (isCartSubscriptionActive) {
      await handleAddToCart(stateName);
      return;
    }
    handlePayAndSubscribe(stateName);
  };

  return (
    <Modal.Root open={showAddonModal} onOpenChange={handleClose}>
      <Modal.Content useCustomOverlay>
        <Modal.Header>
          <Modal.Title>{subscription_name}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Formik<SalesTaxFilingFormValues>
          initialValues={{ selectedState: null }}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting, setFieldValue }) => (
            <Form className="all:unset">
              <Modal.Body>
                <Async.Root
                  isEmpty={!states?.length}
                  isLoading={isLoading || isFetching}
                  isSuccess={isSuccess}
                  renderOnLoad={
                    <div className="t-h-40 t-flex t-items-center t-justify-center">
                      <Loader />
                    </div>
                  }
                >
                  <Async.Empty>
                    <></>
                  </Async.Empty>
                  <Async.Success>
                    <div className="t-flex t-flex-col t-gap-4">
                      <div className="t-text-body-sm">
                        Covers sales tax filings for the selected state under
                        this cadence, launched automatically once subscribed.
                      </div>
                      <div className="t-w-full t-flex t-gap-10 t-items-end">
                        <div className="t-w-[280px]">
                          <Combobox
                            label="State"
                            name="state"
                            placeholder="Select state"
                            options={stateOptions}
                            value={values.selectedState}
                            onChange={(option) =>
                              setFieldValue("selectedState", option)
                            }
                            isOptionDisabled={(option) =>
                              Boolean(option.disableOption)
                            }
                            isClearable={false}
                            tooltip
                            menuPortalTarget={document.body}
                          />
                        </div>
                        <div>
                          <div className="t-text-text-30 t-text-body-sm">
                            Subscription cost
                          </div>
                          <div className="t-text-body-sm">
                            <Price
                              size="small"
                              cycle={billing_cycle!}
                              amount={updatedAmount}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Async.Success>
                </Async.Root>
              </Modal.Body>
              <Modal.Footer className="t-flex t-justify-end t-gap-3">
                <Button type="button" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  customType="primary"
                  isLoading={isSubmitting || isAddingToCart}
                  disabled={
                    !values.selectedState || isSubmitting || isAddingToCart
                  }
                >
                  {isCartSubscriptionActive ? "Add to cart" : "Pay & subscribe"}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
